import { db } from './firebase';
import {
  doc, getDoc, setDoc, updateDoc, deleteDoc, onSnapshot,
  collection, getDocs, serverTimestamp,
} from 'firebase/firestore';

// 기기 코드 승인(화이트리스트)
// 컬렉션: device_approvals, 문서ID = 기기ID(localStorage 발급)
//   { deviceId, mcode, userName, deviceLabel, status:'pending'|'approved', code, createdAt, approvedAt, lastSeen }
// 흐름: 로그인 → 미승인 기기면 pending 등록 + 코드입력 게이트 → 관리자가 코드 생성 → 사용자 입력 일치 시 approved.
// 한번 approved 되면 같은 기기ID(localStorage 유지)로는 계속 통과. 관리자 계정은 게이트 면제.
const COL = 'device_approvals';
const DEVICE_ID_KEY = 'plhouse.deviceId';

// 기기 고유 ID — localStorage 발급(웹은 하드웨어 UDID 없음). 캐시 삭제/재설치 시 새 기기로 잡힘.
export function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = 'dev_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch (e) {
    return 'dev_nostorage';
  }
}

// 기기 라벨(표시용) — userAgent 우선(iPad/태블릿/폰), 화면폭은 보조
function deviceLabel() {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  const mtp = (typeof navigator !== 'undefined' && navigator.maxTouchPoints) || 0;
  // iPadOS 13+ 는 UA가 Macintosh로 위장 → 터치 포인트로 구분
  const isIpad = /iPad/.test(ua) || (/Macintosh/.test(ua) && mtp > 1);
  const isAndroidTablet = /Android/.test(ua) && !/Mobile/.test(ua);
  if (/iPhone|iPod/.test(ua) || (/Android/.test(ua) && /Mobile/.test(ua))) return '핸드폰';
  if (isIpad || isAndroidTablet) return '패드';
  const w = (typeof window !== 'undefined' && window.innerWidth) || 0;
  return w < 768 ? '핸드폰' : w <= 1024 ? '패드' : 'PC';
}

// 미승인이면 대기 등록(없을 때만 생성), 있으면 사용자/접속정보만 갱신
export async function ensurePendingDevice(deviceId, mcode, userName) {
  const ref = doc(db, COL, deviceId);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await setDoc(ref, {
      mcode: String(mcode || ''), userName: userName || '',
      deviceLabel: deviceLabel(), lastSeen: serverTimestamp(),
    }, { merge: true });
    return { id: ref.id, ...snap.data() };
  }
  const data = {
    deviceId, mcode: String(mcode || ''), userName: userName || '',
    deviceLabel: deviceLabel(), status: 'pending', code: '',
    createdAt: serverTimestamp(), lastSeen: serverTimestamp(),
  };
  await setDoc(ref, data);
  return { id: ref.id, ...data };
}

// 실시간 구독 — 관리자가 코드 생성/승인하면 게이트가 즉시 반응
export function subscribeDeviceApproval(deviceId, cb) {
  return onSnapshot(doc(db, COL, deviceId), (snap) => {
    cb(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
}

// 사용자 코드 입력 → 일치 시 승인
export async function verifyDeviceCode(deviceId, inputCode) {
  const ref = doc(db, COL, deviceId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return { ok: false, reason: 'no_device' };
  const d = snap.data();
  if (!d.code) return { ok: false, reason: 'no_code' };
  if (String(inputCode).trim() !== String(d.code).trim()) return { ok: false, reason: 'mismatch' };
  await updateDoc(ref, { status: 'approved', approvedAt: serverTimestamp() });
  return { ok: true };
}

// ── 관리자 ──
export async function fetchDeviceApprovals() {
  const snap = await getDocs(collection(db, COL));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}
// 4자리 코드 생성(관리자가 불러줄 번호)
export async function generateDeviceCode(deviceId) {
  const code = String(Math.floor(1000 + Math.random() * 9000));
  await updateDoc(doc(db, COL, deviceId), { code, status: 'pending' });
  return code;
}
export async function setDeviceStatus(deviceId, status) {
  await updateDoc(doc(db, COL, deviceId), { status });
}
export async function deleteDeviceApproval(deviceId) {
  await deleteDoc(doc(db, COL, deviceId));
}
